/**
 * Elementor Audio Player Widget
 * 
 * Source file for the audio player functionality
 */

import '../scss/audio-player.scss';

(function($) {
    'use strict';

    // AudioPlayer Class
    class ElementorAudioPlayer {
        constructor(element) {
            // DOM Elements
            this.element = element;
            this.playerContainer = element.querySelector('.eap-player-container');
            this.waveformContainer = element.querySelector('.eap-waveform-container');
            this.playPauseButton = element.querySelector('.eap-play-pause');
            this.volumeButton = element.querySelector('.eap-volume');
            this.volumeSlider = element.querySelector('.eap-volume-slider-inner');
            this.currentTimeDisplay = element.querySelector('.eap-current-time');
            this.durationDisplay = element.querySelector('.eap-duration');
            
            // Parse settings
            this.settings = JSON.parse(element.dataset.settings || '{}');
            
            // State
            this.isPlaying = false;
            this.volume = localStorage.getItem('eap-volume') || 1;
            this.isMuted = localStorage.getItem('eap-muted') === 'true';
            
            // Initialize player
            this.init();
        }
        
        init() {
            // Initialize WaveSurfer
            this.initWaveSurfer();
            
            // Initialize event listeners
            this.initEvents();
            
            // Update UI with initial state
            this.updateVolumeUI();
        }
        
        initWaveSurfer() {
            // Create WaveSurfer instance
            this.wavesurfer = WaveSurfer.create({
                container: this.waveformContainer,
                waveColor: this.settings.waveformColor || '#d1d1d1',
                progressColor: this.settings.waveformProgressColor || '#007AFF',
                cursorColor: this.settings.waveformCursorColor || '#FF2D55',
                barWidth: this.settings.barWidth || 2,
                barGap: this.settings.barGap || 1,
                barRadius: 3,
                cursorWidth: 1,
                height: 40,
                responsive: true,
                normalize: true,
                pixelRatio: 1,
                barMinHeight: 1,
                interact: true,
                hideScrollbar: true,
                partialRender: true
            });
            
            // Load audio
            this.wavesurfer.load(this.settings.audioUrl);
            
            // Set volume
            this.wavesurfer.setVolume(this.isMuted ? 0 : this.volume);
            
            // Wavesurfer events
            this.wavesurfer.on('ready', () => {
                // Update duration display
                this.updateDurationDisplay();
                
                // Autoplay if enabled
                if (this.settings.autoplay) {
                    this.play();
                }
                
                // Make player visible (prevents FOUC)
                this.element.classList.add('eap-ready');
            });
            
            this.wavesurfer.on('audioprocess', () => {
                this.updateCurrentTimeDisplay();
            });
            
            this.wavesurfer.on('seek', () => {
                this.updateCurrentTimeDisplay();
            });
            
            this.wavesurfer.on('finish', () => {
                if (this.settings.loop) {
                    this.wavesurfer.play(0);
                } else {
                    this.pause();
                }
            });
            
            this.wavesurfer.on('error', (error) => {
                console.error('WaveSurfer error:', error);
                this.element.classList.add('eap-error');
            });
        }
        
        initEvents() {
            // Play/Pause button
            this.playPauseButton.addEventListener('click', () => {
                this.togglePlayPause();
            });
            
            // Volume button
            this.volumeButton.addEventListener('click', () => {
                this.toggleMute();
            });
            
            // Volume slider (using noUiSlider or similar would be better for production)
            if (this.volumeSlider) {
                this.volumeSlider.addEventListener('click', (event) => {
                    const rect = this.volumeSlider.getBoundingClientRect();
                    const volumePercent = (event.clientX - rect.left) / rect.width;
                    this.setVolume(volumePercent);
                });
            }
            
            // Keyboard controls
            document.addEventListener('keydown', (event) => {
                // Only handle keyboard events when player is in viewport
                if (this.isInViewport()) {
                    if (event.code === 'Space') {
                        event.preventDefault();
                        this.togglePlayPause();
                    } else if (event.code === 'ArrowUp') {
                        event.preventDefault();
                        this.increaseVolume();
                    } else if (event.code === 'ArrowDown') {
                        event.preventDefault();
                        this.decreaseVolume();
                    } else if (event.code === 'ArrowLeft') {
                        event.preventDefault();
                        this.skipBackward();
                    } else if (event.code === 'ArrowRight') {
                        event.preventDefault();
                        this.skipForward();
                    }
                }
            });
            
            // Handle sticky player if enabled
            if (this.settings.sticky === 'yes') {
                window.addEventListener('scroll', () => {
                    this.handleStickyPlayer();
                });
            }
        }
        
        // Playback Controls
        play() {
            this.wavesurfer.play();
            this.isPlaying = true;
            this.updatePlayPauseUI();
        }
        
        pause() {
            this.wavesurfer.pause();
            this.isPlaying = false;
            this.updatePlayPauseUI();
        }
        
        togglePlayPause() {
            if (this.isPlaying) {
                this.pause();
            } else {
                this.play();
            }
        }
        
        // Volume Controls
        setVolume(value) {
            this.volume = Math.max(0, Math.min(1, value));
            this.isMuted = this.volume === 0;
            
            this.wavesurfer.setVolume(this.volume);
            localStorage.setItem('eap-volume', this.volume);
            localStorage.setItem('eap-muted', this.isMuted);
            
            this.updateVolumeUI();
        }
        
        increaseVolume() {
            this.setVolume(Math.min(1, this.volume + 0.1));
        }
        
        decreaseVolume() {
            this.setVolume(Math.max(0, this.volume - 0.1));
        }
        
        toggleMute() {
            if (this.isMuted) {
                this.isMuted = false;
                this.wavesurfer.setVolume(this.volume);
            } else {
                this.isMuted = true;
                this.wavesurfer.setVolume(0);
            }
            
            localStorage.setItem('eap-muted', this.isMuted);
            this.updateVolumeUI();
        }
        
        // Navigation Controls
        skipForward() {
            const currentTime = this.wavesurfer.getCurrentTime();
            const newTime = Math.min(this.wavesurfer.getDuration(), currentTime + 5);
            this.wavesurfer.seekTo(newTime / this.wavesurfer.getDuration());
        }
        
        skipBackward() {
            const currentTime = this.wavesurfer.getCurrentTime();
            const newTime = Math.max(0, currentTime - 5);
            this.wavesurfer.seekTo(newTime / this.wavesurfer.getDuration());
        }
        
        // UI Updates
        updatePlayPauseUI() {
            if (this.isPlaying) {
                this.playPauseButton.innerHTML = '<i class="eicon-pause" aria-hidden="true"></i>';
                this.playPauseButton.setAttribute('aria-label', 'Pause');
                this.element.classList.add('eap-is-playing');
            } else {
                this.playPauseButton.innerHTML = '<i class="eicon-play" aria-hidden="true"></i>';
                this.playPauseButton.setAttribute('aria-label', 'Play');
                this.element.classList.remove('eap-is-playing');
            }
        }
        
        updateVolumeUI() {
            if (this.isMuted || this.volume === 0) {
                this.volumeButton.innerHTML = '<i class="eicon-volume-off" aria-hidden="true"></i>';
                this.volumeButton.setAttribute('aria-label', 'Unmute');
            } else if (this.volume < 0.5) {
                this.volumeButton.innerHTML = '<i class="eicon-volume-low" aria-hidden="true"></i>';
                this.volumeButton.setAttribute('aria-label', 'Mute');
            } else {
                this.volumeButton.innerHTML = '<i class="eicon-volume-high" aria-hidden="true"></i>';
                this.volumeButton.setAttribute('aria-label', 'Mute');
            }
            
            // Update volume slider position
            if (this.volumeSlider) {
                this.volumeSlider.style.width = `${this.volume * 100}%`;
            }
        }
        
        updateCurrentTimeDisplay() {
            if (this.currentTimeDisplay) {
                const currentTime = this.wavesurfer.getCurrentTime();
                this.currentTimeDisplay.textContent = this.formatTime(currentTime);
            }
        }
        
        updateDurationDisplay() {
            if (this.durationDisplay) {
                const duration = this.wavesurfer.getDuration();
                this.durationDisplay.textContent = this.formatTime(duration);
            }
        }
        
        handleStickyPlayer() {
            if (this.isPlaying && !this.isInViewport()) {
                this.element.classList.add('eap-sticky-active');
            } else {
                this.element.classList.remove('eap-sticky-active');
            }
        }
        
        // Utility Methods
        formatTime(seconds) {
            const minutes = Math.floor(seconds / 60);
            const remainingSeconds = Math.floor(seconds % 60);
            return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
        }
        
        isInViewport() {
            const rect = this.element.getBoundingClientRect();
            return (
                rect.top >= 0 &&
                rect.left >= 0 &&
                rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                rect.right <= (window.innerWidth || document.documentElement.clientWidth)
            );
        }
        
        // Clean up resources
        destroy() {
            if (this.wavesurfer) {
                this.wavesurfer.destroy();
            }
        }
    }
    
    // Initialize all audio players when DOM is ready
    $(document).ready(function() {
        // Initialize for existing elements
        $('.eap-audio-player').each(function() {
            new ElementorAudioPlayer(this);
        });
        
        // Initialize for dynamically added elements (e.g., in Elementor editor)
        $(document).on('elementor/frontend/init', function() {
            elementorFrontend.hooks.addAction('frontend/element_ready/audio_player.default', function($element) {
                $element.find('.eap-audio-player').each(function() {
                    new ElementorAudioPlayer(this);
                });
            });
        });
    });
    
})(jQuery);